import { useState } from 'react'

import { FiltersState, InFilterDefinition } from '@factorialco/f0-react'
import { TimeInsightsValueObjectsInconsistencyTypeEnum } from 'generated/resources/graphql'

export type StringInFilter = InFilterDefinition<string>

export type NumberInFilter = InFilterDefinition<number>
export type BooleanInFilter = {
  type: 'in'
  label: string
  options: {
    options: {
      label: string
      value: boolean
    }[]
  }
}
export type TimeInsightsInconsistencyTypeFilter = {
  type: 'in'
  label: string
  options: {
    options: {
      label: string
      value: TimeInsightsValueObjectsInconsistencyTypeEnum
    }[]
  }
}

export const managedByEmployeeEnum = {
  managed_by_me: 'managed_by_me',
  managed_by_others: 'managed_by_others',
  managed_directly_by_me: 'managed_directly_by_me',
  managed_timeoff_by_me: 'managed_timeoff_by_me',
}

interface Props {
  currentEmployeeId: number | undefined
  hasTimeTrackingInconsistencies: boolean
}

export const useFilters = ({ currentEmployeeId, hasTimeTrackingInconsistencies }: Props) => {
  const [analyticsFilters, setAnalyticsFilters] = useState<{
    onlyMyReportees: boolean | undefined
    reportingTo: number[] | undefined
    timeInsightsInconsistencyTypes: TimeInsightsValueObjectsInconsistencyTypeEnum[] | undefined
    onlyDirectReports: boolean | undefined
    timeoffManagerIds: number[] | undefined
    onlyActive: boolean | undefined
    teamIds: number[] | undefined
    legalEntityIds: number[] | undefined
    locationIds: number[] | undefined
    hasTimeTrackingInconsistencies: boolean
    onlyNonAttendable: boolean | undefined
    hasWorkedLessThanPlanned: boolean | undefined
  }>({
    onlyMyReportees: undefined,
    reportingTo: undefined,
    timeInsightsInconsistencyTypes: undefined,
    onlyDirectReports: undefined,
    timeoffManagerIds: undefined,
    onlyActive: undefined,
    teamIds: undefined,
    legalEntityIds: undefined,
    locationIds: undefined,
    hasTimeTrackingInconsistencies: false,
    onlyNonAttendable: undefined,
    hasWorkedLessThanPlanned: undefined,
  })
  const updateFilters = (
    filters: FiltersState<{
      team: NumberInFilter
      workplace: NumberInFilter
      legalEntity: NumberInFilter
      reviewStatus: StringInFilter
      directReports: StringInFilter
      active: BooleanInFilter
      reportingTo: NumberInFilter
      alerts: TimeInsightsInconsistencyTypeFilter
      status: StringInFilter
      workedLess: BooleanInFilter
    }>
  ) => {
    const teamIds = filters.team?.map((id) => id)
    const locationIds = filters.workplace?.map((id) => id)
    const legalEntityIds = filters.legalEntity?.map((id) => id)
    const reviewStatus = filters.reviewStatus?.length
      ? filters.reviewStatus
      : ['pending', 'approved', 'in_progress']
    const onlyMyReportees =
      filters.directReports &&
      filters.directReports.length > 0 &&
      (filters.directReports.includes(managedByEmployeeEnum.managed_by_me) ||
        filters.directReports.includes(managedByEmployeeEnum.managed_by_others))
        ? filters.directReports?.includes(managedByEmployeeEnum.managed_by_me)
        : undefined
    const directReports =
      filters.directReports &&
      filters.directReports.length > 0 &&
      filters.directReports.includes(managedByEmployeeEnum.managed_directly_by_me)
        ? true
        : undefined
    const managedTimeoffByMe =
      filters.directReports &&
      filters.directReports.length > 0 &&
      filters.directReports.includes(managedByEmployeeEnum.managed_timeoff_by_me)
        ? [currentEmployeeId ?? 0]
        : undefined

    const onlyNonAttendable = filters.status?.includes('only_non_attendable')
    const hasWorkedLessThanPlanned = filters.workedLess?.includes(true)

    const reportingTo = filters.reportingTo?.map((id) => id)
    const timeInsightsInconsistencyTypes = filters.alerts?.map((alert) => alert)
    const onlyActive = filters.status?.includes('only_active')
    const onlyDirectReports = directReports
    const timeoffManagerIds = managedTimeoffByMe

    setAnalyticsFilters({
      onlyMyReportees,
      reportingTo,
      timeInsightsInconsistencyTypes,
      onlyDirectReports,
      timeoffManagerIds,
      onlyActive,
      teamIds,
      legalEntityIds,
      locationIds,
      hasTimeTrackingInconsistencies,
      onlyNonAttendable,
      hasWorkedLessThanPlanned,
    })

    return {
      onlyMyReportees,
      reportingTo,
      timeInsightsInconsistencyTypes,
      onlyDirectReports,
      timeoffManagerIds,
      onlyActive,
      teamIds,
      legalEntityIds,
      locationIds,
      reviewStatus,
      onlyNonAttendable,
      hasWorkedLessThanPlanned,
    }
  }

  return {
    analyticsFilters,
    updateFilters,
  }
}
